import { Input, Text } from '@/ui-kit'
import { Drawer, DrawerClose, DrawerContent, DrawerHeader, DrawerTitle } from '@/ui-kit/components/Drawer'
import { Trans, useLingui } from '@lingui/react/macro'
import clsx from 'clsx'
import { Check, ChevronDown, X } from 'lucide-react'
import { useEffect, useMemo, useRef, useState } from 'react'
import type { FormSelectOption } from './FormSelect'

type FormSelectMobileDrawerProps = {
  className?: string
  buttonClassName?: string
  value: string | undefined
  valueLabel?: string
  onChange: (value: string) => void
  label: string
  options: readonly FormSelectOption[]
  placeholder: string
  errorMessage?: string
  searchable?: boolean
}

const SEARCH_MIN_OPTIONS = 10

const splitWords = (value: string) =>
  value
    .toLowerCase()
    .trim()
    .split(/[\s\-_/(),.]+/)
    .filter(Boolean)

const matchesByWordPrefix = (label: string, query: string) => {
  const queryWords = splitWords(query)
  if (!queryWords.length) return true

  const labelWords = splitWords(label)
  return queryWords.every((queryWord) => labelWords.some((labelWord) => labelWord.startsWith(queryWord)))
}

export const FormSelectMobileDrawer = ({
  className,
  buttonClassName,
  value,
  valueLabel: valueLabelProp,
  onChange,
  label,
  options,
  placeholder,
  errorMessage,
  searchable = false
}: FormSelectMobileDrawerProps) => {
  const { t } = useLingui()
  const isError = !!errorMessage
  const [isOpen, setIsOpen] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const searchInputRef = useRef<HTMLInputElement>(null)

  const displayLabel = useMemo(() => {
    return valueLabelProp || options.find((opt) => opt.value === value)?.label
  }, [options, value, valueLabelProp])

  const showSearch = searchable && options.length >= SEARCH_MIN_OPTIONS

  const filteredOptions = useMemo(() => {
    if (!searchable || !searchQuery.trim()) return options
    return options.filter((opt) => matchesByWordPrefix(opt.label, searchQuery))
  }, [options, searchable, searchQuery])

  useEffect(() => {
    if (!isOpen) {
      setSearchQuery('')
    }
  }, [isOpen])

  const handleSelect = (next: string) => {
    onChange(next)
    setIsOpen(false)
  }

  return (
    <div className={clsx('flex flex-col gap-1', className)}>
      <button
        type="button"
        onClick={() => setIsOpen(true)}
        className={clsx(
          'border-gray-90 flex h-[56px] w-full flex-col items-start gap-0 rounded-xl border border-solid px-4 py-2 text-left font-medium outline-0 transition-colors',
          'focus:border-gray-70',
          { 'border-red-500!': isError },
          buttonClassName
        )}
      >
        <span className="text-gray-90 text-body-3">{label}</span>
        <div className="flex w-full items-center justify-between gap-2">
          <span className={clsx('line-clamp-1', !displayLabel && 'text-gray-80')}>{displayLabel || placeholder}</span>
          <ChevronDown className="text-gray-80 -mt-4 size-4 shrink-0 opacity-70" />
        </div>
      </button>

      <Drawer open={isOpen} onOpenChange={setIsOpen}>
        <DrawerContent className="max-h-[85vh]">
          <DrawerHeader className="flex flex-row items-center justify-between gap-3 border-b border-solid border-gray-100 px-4 py-3">
            <DrawerTitle className="text-body-2 text-gray-90">{label}</DrawerTitle>
            <DrawerClose
              aria-label={t`Close`}
              className="text-gray-80 hover:text-gray-70 -m-2 flex h-10 w-10 cursor-pointer items-center justify-center rounded-lg transition-colors"
            >
              <X className="size-5" />
            </DrawerClose>
          </DrawerHeader>

          {showSearch && (
            <div className="sticky top-0 z-10 border-b border-solid border-gray-100 p-3">
              <Input
                className="text-[16px]"
                ref={searchInputRef}
                value={searchQuery}
                onChange={setSearchQuery}
                placeholder={t`Search...`}
              />
            </div>
          )}

          <div className="scrollbar min-h-0 flex-1 overflow-y-auto p-2 pb-6">
            {filteredOptions.map((opt) => {
              const isSelected = opt.value === value
              return (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => handleSelect(opt.value)}
                  className={clsx(
                    'text-body-2 flex w-full cursor-pointer items-center justify-between gap-2 rounded-lg py-3 pr-3 pl-4 text-left transition-colors outline-none',
                    'hover:bg-gray-100/60',
                    isSelected && 'text-orange bg-gray-100/60'
                  )}
                >
                  <span className="line-clamp-2">{opt.label}</span>
                  {isSelected && <Check className="text-orange size-4 shrink-0" />}
                </button>
              )
            })}

            {showSearch && filteredOptions.length === 0 && (
              <div className="text-gray-80 text-body3-regular py-6 text-center">
                <Trans>No matches</Trans>
              </div>
            )}
          </div>
        </DrawerContent>
      </Drawer>

      {errorMessage && (
        <Text type="body3-regular" className="text-red-500">
          {errorMessage}
        </Text>
      )}
    </div>
  )
}
